import DetailsModel, { DetailMetricType, NestedTreeModel } from "src/shared/models/DetailsModel";
import { getTimeFromTimestamp, safeGet, TimeDifferenceGA } from "src/shared/utils";
import { IActionFactory, IObject } from "src/store";
import { createActionWithPayload, createReduxOperation, makeConstants } from "src/store/utils";
import { NotificationType, ThunkResult } from "src/shared/types";
import * as api from "src/api/details";
import { getCharts } from "src/api/common";
import { getManageMetric, getManageMetricDetails } from "src/api/manageMetrics";
import { fetchFiltersConfig, UserRoles } from "src/store/app";
import { SelectedFilterValues } from "src/shared/components/FiltersPanev2";
import { FetchDetailsPayload, FetchDistFiltersPayload } from "./types";
import { IsTabHidden } from "src/shared/utils/customTab";
import { setAlert } from "src/shared/utils/alert";
import { getServiceName, ServiceName } from "src/utils/getservicename";
import { devLogging } from "src/utils/logger";
import { isL2 } from "src/utils/misRoleUtils";

export const DetailActionsConstants = makeConstants(["SET_DETAILS_FILTERS", "SET_DIMENSIONS"]);

export const setFilters: IActionFactory<symbol, any> = createActionWithPayload(
  DetailActionsConstants.get("SET_DETAILS_FILTERS")!
);

export const setDimensions = createActionWithPayload<symbol, Array<string>>(
  DetailActionsConstants.get("SET_DIMENSIONS") as symbol
);

const {
  actions: [detailsRequest, detailsSuccess, detailsFailure, clearDetails],
  reducer: detailsReducer,
} = createReduxOperation("METRIC_DETAILS");

const {
  actions: [filtersRequest, filtersSuccess, filtersFailure, filtersReset],
  reducer: filtersReducer,
} = createReduxOperation("FETCH_DISTRIBUTED_FILTERS");

const {
  actions: [addPinRequest, addPinSuccess, addPinFailure],
  reducer: addPinReducer,
} = createReduxOperation("ADD_PIN");

const {
  actions: [tableChartsRequest, tableChartsSuccess, tableChartsFailure],
  reducer: tableChartsReducer,
} = createReduxOperation("FETCH_TABLE_CHARTS");

/**
 * If filter is set in L2 page and user navigates to L3 page,
 * the filters should be retained.
 * Hence, an action is dispatched on load of L3 page, that copies L2 filters to L3
 * Only if filter doesnt already exist on L3 page
 * (ie in cases user has updated filters on L3 page or filters are obtained from pinned metric)
 */
export function setFiltersFromL2Page(): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const l3Filters = getState().metricDetails.filters;
    const l2Filters = getState().category.filters;
    let l3FiltersExist = false;
    for (let key in l3Filters) {
      if (l3Filters[key]?.length > 0) {
        l3FiltersExist = true;
      }
    }
    const filter = l3FiltersExist ? l3Filters : l2Filters;
    devLogging(" dispatching filter", filter);
    dispatch(setFilters(filter));
  };
}

/**
 * Builds and fires a second render_filter_slices call for "until last hour" data.
 * Uses the backend's lastUpdatedTimeStamp (not device time) so the cutoff
 * is consistent with the "updated ..." label the user sees.
 * Returns the lastHour chart data array, or null on failure.
 */
async function fetchLastHourCharts(
  bulkChartRequest: string,
  lastUpdatedTimestampMs: number,
  timeTab: string,
  shouldFetchFreshData: boolean
): Promise<any[] | null> {
  const ts = new Date(lastUpdatedTimestampMs);
  if (isNaN(ts.getTime())) return null;

  // Backend stores IST time in UTC fields (getTimeFromTimestamp relies on this).
  const istHour = ts.getUTCHours();
  const istMinute = ts.getUTCMinutes();

  const cutoffHour = istMinute >= 30 ? istHour : istHour - 1;
  if (cutoffHour < 0) return null;

  // Build absolute "to" timestamps so Compass doesn't evaluate "now" on its own clock.
  // This avoids the 5-7 min data leak that relative expressions cause.
  const year = ts.getUTCFullYear();
  const month = String(ts.getUTCMonth() + 1).padStart(2, "0");
  const day = String(ts.getUTCDate()).padStart(2, "0");
  const hourStr = String(cutoffHour).padStart(2, "0");
  const absoluteTo = `${year}-${month}-${day} ${hourStr}:00:00`;

  const pastDate = new Date(ts.getTime() - 7 * 86400000);
  const pastYear = pastDate.getUTCFullYear();
  const pastMonth = String(pastDate.getUTCMonth() + 1).padStart(2, "0");
  const pastDay = String(pastDate.getUTCDate()).padStart(2, "0");
  const absoluteToPast = `${pastYear}-${pastMonth}-${pastDay} ${hourStr}:00:00`;

  let baseRequests: any[] = [];
  try {
    baseRequests = JSON.parse(bulkChartRequest);
  } catch (e) {
    return null;
  }

  const lastHourRequests = baseRequests
    .filter(
      (r) =>
        r?.time_view === "currentChartId" ||
        r?.time_view === "pastChartId" ||
        r?.time_view === "planOrderLineChartId"
    )
    .map((r) => {
      const cloned = JSON.parse(JSON.stringify(r));
      const chronicFilters = safeGet(cloned, "filters.chronic_filters", {});
      if (cloned.time_view === "currentChartId") {
        cloned.time_view = "lastHourCurrentChartId";
        cloned.filters.chronic_filters = { ...chronicFilters, to: absoluteTo };
      } else if (cloned.time_view === "pastChartId") {
        cloned.time_view = "lastHourPastChartId";
        cloned.filters.chronic_filters = { ...chronicFilters, to: absoluteToPast };
      } else {
        cloned.time_view = "lastHourPlanOrderLineChartId";
        cloned.filters.chronic_filters = { ...chronicFilters, to: absoluteTo };
      }
      return cloned;
    });

  if (!lastHourRequests.length) return null;

  try {
    const { response } = await getCharts({
      data: JSON.stringify(lastHourRequests),
      timeTab,
      shouldFetchFreshData,
    });
    return response?.data ?? null;
  } catch (e) {
    devLogging("lastHour charts call failed", e);
    return null;
  }
}

/**
 * Fetches chart data based on metadata (slice ids) fetched on page load
 * the chart data is used for calculations to show current value and % change from previous value
 * the calculated data is added to the metric info obtained in the first call (metadata)
 * Note that the action to execute this thunk is dispatched from another thunk that fetches the metric metadata
 */
function fetchL3TopCharts(payload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const { bulkChartRequest, parsedResponse, timeTab, fresh: shouldFetchFreshData } = payload as IObject<any>;

    const isRenderLastHour = safeGet(parsedResponse, "isRenderLastHour", false);

    const { response: chartsResponse } = await getCharts({
      data: bulkChartRequest,
      timeTab,
      shouldFetchFreshData,
    });

    devLogging("chart response ", chartsResponse?.data);
    if (chartsResponse?.data) {
      const lastUpdatedTimeStamp = getTimeFromTimestamp(chartsResponse.lastUpdatedTimeStamp);
      (parsedResponse as any).lastUpdatedTimeStamp = lastUpdatedTimeStamp;
      (parsedResponse as any).lastUpdatedTimeStampRaw = chartsResponse.lastUpdatedTimeStamp;

      // Second call: fetch "until last hour" data using lastUpdatedTimeStamp as the single source of truth
      let allChartData = chartsResponse.data;
      if (isRenderLastHour && timeTab === "Today") {
        const lastHourData = await fetchLastHourCharts(
          bulkChartRequest,
          chartsResponse.lastUpdatedTimeStamp,
          timeTab,
          shouldFetchFreshData
        );
        if (lastHourData) {
          allChartData = chartsResponse.data.concat(lastHourData);
        }
      }

      const mergedResponse = parsedResponse.calculateDataFromCharts(
        allChartData,
        timeTab
      );

      const chartOrPinId = window.location.href.split("/")?.at(-1);
      if ((chartOrPinId && chartOrPinId != mergedResponse.id) || getState().app.selectedTab !== timeTab) {
        devLogging(
          "worst piece of code imaginable, all in one file. This repo should be circulated in swiggy on how to not write code."
        );
        return;
      }

      mergedResponse.lastUpdatedTimeStamp = lastUpdatedTimeStamp;
      dispatch(detailsSuccess(mergedResponse));
      if (timeTab == "Today") {
        const currentChartsData = chartsResponse.data.filter((item) => {
          return item["time_view"] == "currentChartId";
        });
        TimeDifferenceGA(currentChartsData, "Metric Details Page");
      }
    } else {
      dispatch(detailsSuccess(parsedResponse));
    }
  };
}

/**
 *
 * To fetch data to be shown on accordion charts on the bottom of L3 page
 * creates request body based on the three dimensions selected by the user
 * makes call to comapss backend (render_filter_slices)
 *
 * The response is flattened info which is parsed to form hierarchical data
 * depending on the order of dimensions selected
 *
 * All the parsing is done in helper functions defined in Model Classes
 */
export function fetchNestedTreeData(payload: FetchDistFiltersPayload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    if (!payload) throw Error("payload expected");

    if (!safeGet(payload, "firstSelectedValue", false)) {
      return;
    }

    const timeTab = getState().app.selectedTab;

    let filters = payload.filters;
    const userRoles: UserRoles = getState().app.userRoles.payload;
    // needs to be done here also since fetchNestedTreeData called when dropdown changes
    if (!userRoles.hasFullAccess && !filters.city?.length) {
      filters = {
        ...filters,
        city: userRoles.cities,
      };
    }

    // check if user have pod access and service is anobis IMCOM && store_id is not present in filters
    if (getServiceName() === ServiceName.ANOBIS_IMCOM && !filters.store_id?.length) {
      filters = { ...filters, store_id: userRoles.pods };
    }

    dispatch(filtersRequest());
    const filterConfig = getState().app.appConfig.payload;
    const body = DetailsModel.constructNestedTreePayload(
      safeGet(payload, "pinId", 0),
      safeGet(payload, "metricId", 0),
      safeGet(payload, "tableChartId", 0),
      safeGet(payload, "firstSelectedValue", false),
      safeGet(payload, "secondSelectedValue", false),
      safeGet(payload, "lastSelectedValue", false),
      timeTab,
      filters,
      filterConfig
    );

    const { response: filtersResponse, error: filtersError } = await getCharts({
      data: JSON.stringify(body),
      timeTab,
      shouldFetchFreshData: payload?.fresh,
    });
    if (filtersResponse) {
      dispatch(
        filtersSuccess(
          NestedTreeModel.constructModel(filtersResponse.data || [], {
            FIRST_DIMENSION: payload!.firstSelectedValue,
            SECOND_DIMENSION: payload!.secondSelectedValue,
            THIRD_DIMENSION: payload!.lastSelectedValue,
          })
        )
      );
    } else dispatch(filtersFailure(filtersError));
  };
}

function isCityFilterValid(appliedFilterCities: string[], userCities: string[]): boolean {
  const invalidCities = appliedFilterCities.filter((city) => !userCities.includes(city));

  if (!invalidCities.length) return true;

  setAlert(NotificationType.ERROR, `User doesn't have access to cities: ${invalidCities.join(", ")}`);
  setAlert(NotificationType.SUCCESS, `City filters not applied, showing data for all cities user has access to`);
  return false;
}

/**
 * On load of L3 page, the metric metadata is fetched from this thunk
 * the api endpoint differs based on metric type (pinned metric or metric)
 */
export function fetchDetails({
  fresh,
  metricId,
  metricType,
  filters,
}: FetchDetailsPayload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    const shouldFetchFreshData = fresh;
    const userRoles: UserRoles = getState().app.userRoles.payload;
    // below code will override pinned metric filters at first render. So, commented
    // if (!userRoles.hasFullAccess && !filters?.city?.length) {
    //   filters = { ...filters, city: userRoles.cities };
    // }

    // const filtersString = formFilterString(filters);
    dispatch(detailsRequest());
    const timeTab = getState().app.selectedTab;

    devLogging("time tab value", timeTab);

    let apiResponse;
    if (metricType === DetailMetricType.PIN) {
      apiResponse = await getManageMetric({ timeTab, shouldFetchFreshData, metricId });
    } else {
      apiResponse = await getManageMetricDetails({ timeTab, shouldFetchFreshData, metricId });
    }

    devLogging("log api response ", apiResponse);

    const { response: detailsResponse, error: detailsError } = apiResponse;
    if (detailsResponse) {
      const parsedResponse = DetailsModel.createDetail(detailsResponse);

      let updateFilters: SelectedFilterValues = {};
      // Will be true at first render of pin detail & on refresh click
      // At first render and on refresh click, use filters from pin instead of applied ones

      if (metricType === DetailMetricType.PIN && parsedResponse.filtersFromPin && filters === undefined) {
        updateFilters = parsedResponse.filtersFromPin;
        dispatch(setFilters(parsedResponse.filtersFromPin));
      } else {
        updateFilters = filters || {};
      }

      devLogging("here is the updated filter ", updateFilters);

      // check if user doesn't have access to city, that is applied for filter
      if (updateFilters.city?.length && !userRoles.hasFullAccess) {
        const { city: appliedFilterCities } = updateFilters;
        const { cities: userCities } = userRoles;
        if (!isCityFilterValid(appliedFilterCities, userCities)) {
          updateFilters.city = [];
        }
      }

      // check if user have pod access and service is anobis IMCOM
      if (getServiceName() === ServiceName.ANOBIS_IMCOM && !updateFilters.store_id?.length) {
        updateFilters = { ...updateFilters, store_id: userRoles.pods };
      }

      if (getServiceName() === ServiceName.ANOBIS_INDIA_2) {
        updateFilters = {
          ...updateFilters,
          // Don't include restaurant_id for L2 users
          ...(isL2(userRoles.misRole) 
            ? {} 
            : { restaurant_id: updateFilters.restaurant_id?.length > 0 ? updateFilters.restaurant_id : userRoles.restaurantIds }),
          city: updateFilters.city?.length > 0 ? updateFilters.city : userRoles.cities,
          poc_email_id: updateFilters.poc_email_id?.length > 0 ? updateFilters.poc_email_id : [],
          // Include l1_manager_email_id for L2 users if available
          ...(isL2(userRoles.misRole) && updateFilters.l1_manager_email_id?.length > 0 
            ? { l1_manager_email_id: updateFilters.l1_manager_email_id } 
            : {}),
        };
      }

      if (!userRoles.hasFullAccess && !updateFilters.city?.length) {
        updateFilters = { ...updateFilters, city: userRoles.cities };
      }

      const filterConfig = getState().app.appConfig.payload;
      const bulkChartRequest = DetailsModel.getDetailsBulkChartRequest(
        parsedResponse,
        updateFilters,
        timeTab,
        filterConfig,
        metricType
      );

      devLogging(
        "bulk chart request bulk chart request ",
        bulkChartRequest,
        parsedResponse,
        timeTab,
        filterConfig,
        metricType
      );

      dispatch(fetchL3TopCharts({ bulkChartRequest, parsedResponse, timeTab, fresh: shouldFetchFreshData }));

      await dispatch(
        fetchFiltersConfig({
          metricId:
            metricType === DetailMetricType.PIN ? parsedResponse.metricId?.toString() : parsedResponse.id.toString(),
          fresh: shouldFetchFreshData,
        })
      );
      const selectedDimensionsInitial = getState().metricDetails.selectedDimensions;
      if (selectedDimensionsInitial?.length === 0) {
        dispatch(setDimensions(parsedResponse.defaultSelectedDimensions.map((d) => d.id)));
      }

      /** Make call for the NestedTree */
      const selectedDimensions = getState().metricDetails.selectedDimensions;
      dispatch(
        fetchNestedTreeData({
          tableChartId: !IsTabHidden(detailsResponse.isHidden, timeTab) ? detailsResponse.tableChartId : -1,
          filters: updateFilters,
          selectedTab: timeTab,
          firstSelectedValue: safeGet(selectedDimensions, "0"),
          secondSelectedValue: safeGet(selectedDimensions, "1"),
          lastSelectedValue: safeGet(selectedDimensions, "2"),
          metricId: parsedResponse.metricId as number,
          fresh: shouldFetchFreshData,
          ...(metricType === DetailMetricType.PIN && { pinId: parsedResponse.id }),
        })
      );
    } else {
      dispatch(detailsFailure(detailsError));
    }
  };
}

export function addPin(payload: string): ThunkResult<Promise<void>> {
  return async (dispatch) => {
    try {
      dispatch(addPinRequest());
      const response = await api.addPin(payload);
      if (response) {
        dispatch(addPinSuccess(response));
      }
    } catch (e) {
      dispatch(addPinFailure(e));
    }
  };
}

/**
 * Fetches table charts data based on metadata (slice ids) fetched on page load.
 * Majorly for showing the 2nd screen table data
 */
export function fetchTableCharts(payload): ThunkResult<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(tableChartsRequest());

    const { tableChartId, filters, metricId, fresh: shouldFetchFreshData } = payload as IObject<any>;

    devLogging("logging payload in fetch table charts ", filters, tableChartId, metricId);

    const userRoles: UserRoles = getState().app.userRoles.payload;

    const misTableChartChronicFilters = {
      from: "today-1day",
      to: "now",
      granularity: "1 hour",
      time_compare: [null],
    };

    const imComTableChartChronicFilters = {
      from: "today",
      to: "now",
      granularity: "1 hour",
      time_compare: [null],
    };

    const tableChartChronicFilters =
      getServiceName() === ServiceName.ANOBIS_INDIA_2 ? misTableChartChronicFilters : imComTableChartChronicFilters;

    const { response: chartsResponse } = await getCharts({
      data: [
        {
          slice_id: tableChartId,
          metric_id: metricId,
          time_view: "filters",
          filters: {
            adhoc_filters: filters,
            chronic_filters: tableChartChronicFilters,
          },
        },
      ],
      timeTab: "Today",
      shouldFetchFreshData,
    });

    devLogging("table chart response ", chartsResponse);

    // Prevent some earlier metric/tab data from replacing redux store value of new metric/tab data due to slow response (Race condition)
    // const chartOrPinId = window.location.href.split("/")?.at(-1);
    // if ((chartOrPinId && chartOrPinId != mergedResponse.id) || getState().app.selectedTab !== timeTab) {
    //   return;
    // }

    // mergedResponse.lastUpdatedTimeStamp = getTimeFromTimestamp(chartsResponse.lastUpdatedTimeStamp);
    try {
      let parsedData = null;
      if (chartsResponse?.data && chartsResponse?.data[0]?.data) {
        const rawData = chartsResponse?.data[0]?.data;
        parsedData = typeof rawData === 'string' ? JSON.parse(rawData)?.data : rawData?.data;
      }

      dispatch(tableChartsSuccess(parsedData || {}));
    } catch (error) {
      console.error("Error parsing JSON data:", error);
      dispatch(tableChartsFailure(error));
    }
    // } else {
    //   //If the call fails, page still renders with the metric metadata,
    //   // and error messages where charts are supposed to be
    //   dispatch(tableChartsFailure(parsedResponse));
    // }
  };
}

export { detailsReducer, filtersReducer, addPinReducer, tableChartsReducer, clearDetails, filtersReset };
